"use client";

/**
 * RODO / cookie consent - klienckie operacje przeglądarkowe.
 *
 * Zapis cookie zgody + zastosowanie zgody w przeglądarce (Google Consent Mode
 * 'update' + injekcja kodów `head`/`body` zaakceptowanych kategorii). Wywoływane
 * po kliknięciu w bannerze. Server-side analogiem jest `RodoConsent` (re-apply
 * przy SSR dla powracających odwiedzających) - oba używają tego samego markera
 * sentinela `imperial-inject-*`, więc injekcja nie dubluje się.
 *
 * @see ./consent.ts - logika współdzielona (resolvery, build skryptu)
 */

import type { RodoCategory } from "@/lib/api/cms-config";
import {
  CONSENT_COOKIE,
  CONSENT_MAX_AGE,
  acceptedCategories,
  grantedTypes,
  serializeConsent,
  type ConsentState,
} from "./consent";

/** Odczyt cookie po stronie klienta (DOM API - brak js-cookie w projekcie). */
export function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/** Zapis cookie zgody (path=/, samesite=Lax, 1 rok). */
export function writeConsentCookie(state: ConsentState): void {
  const value = encodeURIComponent(serializeConsent(state));
  document.cookie = `${CONSENT_COOKIE}=${value}; path=/; max-age=${CONSENT_MAX_AGE}; samesite=Lax`;
}

/**
 * Consent Mode 'update' (granted) dla podanych typów. Preferuje globalny `gtag`
 * zdefiniowany przez skrypt `beforeInteractive` (app/layout.tsx); fallback do
 * `dataLayer.push` gdy gtag jeszcze nie istnieje (np. moduł aktywny ale GTM
 * niezaładowany).
 */
function gtagConsentUpdate(types: string[]): void {
  if (!types.length) return;
  const granted: Record<string, "granted"> = {};
  for (const t of types) {
    if (/^[a-z_]+$/i.test(t)) granted[t] = "granted";
  }
  const w = window as unknown as {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  };
  if (typeof w.gtag === "function") {
    w.gtag("consent", "update", granted);
  } else {
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push(["consent", "update", granted]);
  }
}

/**
 * Wstrzykuje surowy HTML/script do `target` (head/body) z dedupe przez sentinel.
 * Klonuje `<script>` ręcznie (innerHTML nie wykonuje skryptów). Marker zgodny z
 * `CustomHtmlInjector` i `RodoConsent`, więc brak podwójnej injekcji gdy SSR
 * już wstrzyknął kod dla powracającego odwiedzającego.
 */
function injectHtml(
  html: string | null | undefined,
  target: HTMLElement,
  injectorId: string,
): void {
  if (!html || html.trim() === "") return;
  const marker = `imperial-inject-${injectorId}`;
  if (document.querySelector(`[data-imperial-injector="${marker}"]`)) return;

  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  for (const node of Array.from(tpl.content.childNodes)) {
    if (node instanceof HTMLScriptElement) {
      const s = document.createElement("script");
      for (const a of Array.from(node.attributes)) s.setAttribute(a.name, a.value);
      if (node.textContent) s.textContent = node.textContent;
      target.appendChild(s);
    } else {
      target.appendChild(node);
    }
  }

  const sentinel = document.createElement("meta");
  sentinel.setAttribute("data-imperial-injector", marker);
  document.head.appendChild(sentinel);
}

/**
 * Stosuje zgodę w przeglądarce: Consent Mode 'update' (granted) + injekcja
 * kodów `head`/`body` zaakceptowanych kategorii. Wywoływane po zapisie cookie.
 */
export function applyConsent(categories: RodoCategory[], state: ConsentState): void {
  gtagConsentUpdate(grantedTypes(categories, state));
  for (const c of acceptedCategories(categories, state)) {
    injectHtml(c.head, document.head, `rodo-cat-${c.id}-head`);
    injectHtml(c.body, document.body, `rodo-cat-${c.id}-body`);
  }
}
